import { yupResolver } from '@hookform/resolvers/yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
} from '@mui/material';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { currencyFormatter } from 'core/utils/commonUtils';
import { getMeasureName } from 'core/utils/productUtils';
import useProducts from 'hooks/swr/useProducts';
import useResponsive from 'hooks/useResponsive';
import { differenceWith, isNil, remove } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FormContainer, TextFieldElement, useForm } from 'react-hook-form-mui';
import { object, string } from 'yup';
import ProductTags from '../pages/OrderPage/Projects/ProductTags';
import ProductGroupSelect from './ProductGroupSelect/ProductGroupSelect';
import type { ProductGroupSelectedNode } from './ProductGroupSelect/ProductGroupSelect';
import type {
  GridColDef,
  GridRowSelectionModel,
  GridCallbackDetails,
} from '@mui/x-data-grid';
import type { ProductTypeEnum } from 'models/enums/productTypeEnum';
import type { Product } from 'models/product/product';
import type { ProductGroup } from 'models/product/productGroup';
import type { FC, SyntheticEvent } from 'react';
import type { SubmitHandler } from 'react-hook-form-mui';

interface ProductsFilterData {
  search: string;
}

const ProductSelectDialog: FC<ProductSelectProps> = ({
  open,
  mode,
  onSave,
  onCancel,
  typeFilter,
  ignoreProductIds: ignoredProductIds,
  selectedProductIds: _selectedProductIds,
}) => {
  const fullScreen = useResponsive('down', 'sm');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [searchText, setSearchText] = useState('');
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [selectedRowsData, setSelectedRowsData] = useState<Product[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>();

  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  const schema = object<ProductsFilterData>({
    search: string().max(300),
  }).required();

  const formContext = useForm<ProductsFilterData>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const reset = useCallback((): void => {
    formContext.setValue('search', '');
    setRowSelectionModel([]);
    setSelectedRowsData([]);
    setPaginationModel({
      page: 0,
      pageSize: 50,
    });
  }, [formContext]);

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, formContext, reset]);

  const { data, isLoading } = useProducts({
    pageIndex: paginationModel.page,
    pageSize: paginationModel.pageSize,
    filterText: searchText,
    type: typeFilter,
    ignoreIds: ignoredProductIds,
    groupId: selectedGroupId,
  });

  mode = mode ?? ProductSelectModeEnum.Single;

  const columns: Array<GridColDef<Product>> = [
    {
      field: 'name',
      headerName: 'Название',
      sortable: false,
      flex: 1,
      display: 'flex',
      renderCell: (params) => (
        <Stack direction='row' alignItems='center'>
          <span>{params.value}</span>
          <ProductTags tags={params.row.tags} />
        </Stack>
      ),
    },
    {
      field: 'price',
      headerName: 'Цена',
      sortable: false,
      display: 'flex',
      minWidth: 150,
      renderCell: (params) =>
        `${currencyFormatter.format(params.row.price)}/${getMeasureName(
          params.row.measure,
          true
        )}`,
    },
  ];

  const filter: SubmitHandler<ProductsFilterData> = (data) => {
    setSearchText(data.search);
  };

  const saveClickHandler: VoidFunction = () => {
    onSave(selectedRowsData);
    reset();
  };

  const cancelClickHandler: VoidFunction = () => {
    onCancel();
    reset();
  };

  const selectionModelChangeHandler = (
    newRowSelectionModel: GridRowSelectionModel,
    _: GridCallbackDetails
  ): void => {
    const deleted = differenceWith(
      selectedRowsData,
      newRowSelectionModel,
      (a, b) => a.id === +b
    ).map((p) => p.id);

    const newData = differenceWith(
      newRowSelectionModel,
      selectedRowsData,
      (a, b) => +a === b.id
    ).map((d) => +d);

    remove(selectedRowsData, (p) => deleted.includes(p.id));

    newData.forEach((n) => {
      const product = data?.items?.find((d) => d.id === n);
      if (!isNil(product)) {
        selectedRowsData.push(product);
      }
    });

    setSelectedRowsData(selectedRowsData);

    setRowSelectionModel(newRowSelectionModel);
  };

  const handleNodeSelect = (node?: ProductGroupSelectedNode): void => {
    const id = isNil(node) || node.id <= 0 ? undefined : node.id;
    setSelectedGroupId(id);
  };

  const handleGroupToggle = (_event: SyntheticEvent, itemIds: string[]): void => {
    setExpandedGroups(itemIds);
  };

  function forceSaveHandler(): void {
    if (mode !== ProductSelectModeEnum.Single) {
      return;
    }

    process.env.NODE_ENV === 'production' &&
      process.env.REACT_APP_METRIKA_ACCOUNT &&
      ym(
        +process.env.REACT_APP_METRIKA_ACCOUNT,
        'reachGoal',
        '29095330-6e08-43e0-90a0-738e976af7c7'
      );

    saveClickHandler();
  }

  const groupsPostProcessor = (data: ProductGroup[]): ProductGroup[] => [
    { id: -1, name: 'Товары без группы', groups: [] },
    ...(data ?? []),
  ];

  return (
    <Dialog
      open={open}
      PaperProps={{ sx: { minHeight: '70vh', maxHeight: fullScreen ? null : '70vh' } }}
      onClose={cancelClickHandler}
      maxWidth={'lg'}
      fullWidth
      fullScreen={fullScreen}>
      <DialogTitle>Товары</DialogTitle>
      <DialogContent>
        <FormContainer formContext={formContext} onSuccess={filter}>
          <TextFieldElement
            sx={{ marginBottom: 2 }}
            size='small'
            fullWidth
            name='search'
            placeholder='Поиск'
            type='search'
          />
        </FormContainer>
        <Stack
          alignItems={{ xs: 'stretch', md: 'start' }}
          direction={{ xs: 'column', md: 'row' }}
          gap={2}>
          <Box sx={{ md: { maxWidth: '300px', width: '250px' } }}>
            <ProductGroupSelect
              expandedItems={expandedGroups}
              selectedItems={selectedGroupId?.toString() ?? '-1'}
              onExpandedItemsChange={handleGroupToggle}
              onSelectedItemsChange={handleNodeSelect}
              dataPostprocessor={groupsPostProcessor}
            />
          </Box>
          <DataGrid
            sx={{
              [`&.${gridClasses['root--densityStandard']} .${gridClasses.cell}`]: {
                py: 2,

                [`&.${gridClasses.cellCheckbox}`]: {
                  py: 0,
                },
              },
              [`& .${gridClasses.cell}:focus`]: {
                outline: 'none',
              },
              [`& .${gridClasses.row}`]: {
                cursor: 'pointer',
              },
            }}
            rows={data?.items ?? []}
            rowCount={data?.totalCount ?? 0}
            loading={isLoading}
            getRowHeight={() => 'auto'}
            autoHeight
            onRowDoubleClick={forceSaveHandler}
            disableColumnMenu={true}
            checkboxSelection={mode === ProductSelectModeEnum.Multiple}
            keepNonExistentRowsSelected
            paginationModel={paginationModel}
            paginationMode='server'
            onPaginationModelChange={setPaginationModel}
            columns={columns}
            onRowSelectionModelChange={selectionModelChangeHandler}
            rowSelectionModel={rowSelectionModel}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={saveClickHandler}>Готово</Button>
        <Button onClick={cancelClickHandler}>Отмена</Button>
      </DialogActions>
    </Dialog>
  );
};

export enum ProductSelectModeEnum {
  Single,
  Multiple,
}

export interface ProductSelectProps {
  selectedProductIds?: number[];
  ignoreProductIds?: number[];
  typeFilter?: ProductTypeEnum;
  mode?: ProductSelectModeEnum;
  open: boolean;
  onSave: (selectedProductIds: Product[]) => void;
  onCancel: () => void;
}

export default ProductSelectDialog;
